import React from 'react';
import styled from 'styled-components';

import { HubGuideCollageCard } from './HubGuideCollageCard';
import { RowViewport, useHubRowScroll } from './HubRowScroll';
import {
  HUB_GUIDE_CARD_ROW_WIDTH_CSS,
  HUB_GUTTER_CSS,
  HUB_HEADING_CSS,
  HUB_TRACK_GUTTER_CSS,
} from './hubType';

/**
 * "New guides" (THE-822 / WS5, canvas p03 tile 03) — guides `publishedAt`ed
 * in the last 14 days, newest first, each carrying a near-black NEW pill.
 *
 * THE BADGE IS NOT IN THE PRD. It is only in the design canvas (discrepancy
 * 1 in `THE-816-design-canvas-reference.md`): "cheap, and it is the visible
 * payoff of Kate's approved `publishedAt` backfill." Proceeding with it per
 * that note; flagged again here and in the PR for Kate to veto.
 *
 * SELF-HIDING ON FEWER THAN TWO QUALIFYING GUIDES (PRD p.13), which the
 * caller (`hubSections.newGuideListings`) already enforces — this component
 * renders nothing whenever it is handed fewer than two items, same contract
 * as every other hub row, so a sparse week costs a missing row rather than a
 * one-card row nobody asked to see.
 *
 * GUIDE ROW SIZING (PRD "Rows: mobile vs. desktop"): "one card plus a peek"
 * on mobile, "three cards plus a peek" on desktop — distinct from the
 * product rows' 38vw/two-up measure, because a guide collage card is wider
 * than a product photo.
 */

// NO MAX-WIDTH, NO MARGIN:AUTO (THE-1161) — see HUB_GUTTER_CSS's doc comment
// in hubType.ts. $wideGutter-gated: pre-PR this box carried its own
// max-width:1400px/margin:0 auto cap — reproduced below when the flag is off.
const Section = styled.section<{ $wideGutter?: boolean }>`
  box-sizing: border-box;
  ${({ $wideGutter }) => !$wideGutter && 'max-width: 1400px; margin: 0 auto;'}
  width: 100%;
  padding: 16px 0 8px;
`;

const HeadingRow = styled.div<{ $wideGutter?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  ${HUB_GUTTER_CSS}
  margin-bottom: 12px;
`;

const Heading = styled.h2`
  ${HUB_HEADING_CSS}
`;

const Track = styled.div<{ $wideGutter?: boolean }>`
  box-sizing: border-box;
  display: flex;
  gap: 16px;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
  ${HUB_TRACK_GUTTER_CSS}
  padding-top: 2px;
  padding-bottom: 12px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (min-width: 640px) {
    /* Option B (THE-859, board override of the canvas). */
    gap: 20px;
  }
  @media (min-width: 1024px) {
    /* THE-1161 round 8: the one shared desktop gap across every hub row
       (board, 2026-10-27), matching HubPersonGuidesRow's own since both
       share HUB_GUIDE_CARD_ROW_WIDTH_CSS's re-fit width. $wideGutter-gated:
       pre-PR had no 1024px override, so the row stayed at the 640px step's
       20px — reasserted below when the flag is off. No nested css-tagged
       template here (a source fitness test extracts this block by its
       first closing backtick). */
    gap: ${({ $wideGutter }) => ($wideGutter ? '24px' : '20px')};
  }
`;

/* THE-876: the shared standard guide-card width — 69vw peek on phone (THE-870's
   occasion-tile fraction), instead of this row's own formula, so "New guides"
   cannot drift from any other guide-card row again. THE-1161 (Kate's
   "about a third" peek decision, see hubType.ts) layers a >=1024px clamp on
   top via HUB_GUIDE_CARD_ROW_WIDTH_CSS rather than widening the shared
   constant itself — that constant also sizes rows outside this ticket's
   scope (the homepage rail, the board rail, search, the collection tray). */
const Cell = styled.div<{ $wideGutter?: boolean }>`
  ${HUB_GUIDE_CARD_ROW_WIDTH_CSS}
  scroll-snap-align: start;
`;

export interface HubNewGuidesRowSection {
  key: string;
  href: string;
  title: string;
  emoji: string;
  imageUrls: string[];
  /** THE-1395: passed the native click event so the caller can tell a plain
   *  tap from a ⌘/Ctrl/middle-click (see `DiveDeeperBlock`'s identical check). */
  onClick?: (event: React.MouseEvent) => void;
  linkState?: unknown;
}

export interface HubNewGuidesRowProps {
  sections: HubNewGuidesRowSection[];
  headingId?: string;
  heading?: string;
  minimumGuides?: number;
  badge?: boolean;
  /**
   * THE-1161 (rides the Gift Guide hub gate; no preview switch of its own) — defaults to `false` so
   * every existing story/test keeps rendering today's production layout
   * when the hub gate is off. `GiftGuideHub.tsx` resolves the flag once and threads
   * it down; this component stays Firebase/Router-free, matching every
   * other gate in the hub.
   */
  wideGutterEnabled?: boolean;
}

export const HubNewGuidesRow: React.FC<HubNewGuidesRowProps> = ({
  sections,
  headingId = 'hub-new-guides-heading',
  heading = 'New guides',
  minimumGuides = 2,
  badge = true,
  wideGutterEnabled = false,
}) => {
  const row = useHubRowScroll(heading, sections.length);
  if (sections.length < minimumGuides) return null;
  return (
    <Section aria-labelledby={headingId} $wideGutter={wideGutterEnabled}>
      <HeadingRow $wideGutter={wideGutterEnabled}>
        <Heading id={headingId}>{heading}</Heading>
        {/* THE-859 UPDATE: no floating "›" beside the title — the arrow
            moved to the row's edges (RowViewport/edgeArrows below). */}
      </HeadingRow>
      <RowViewport>
      <Track ref={row.ref} $wideGutter={wideGutterEnabled}>
        {sections.map((section) => (
          <Cell key={section.key} $wideGutter={wideGutterEnabled}>
            <HubGuideCollageCard
              href={section.href}
              title={section.title}
              emoji={section.emoji}
              imageUrls={section.imageUrls}
              onClick={section.onClick}
              linkState={section.linkState}
              badge={badge}
              large
            />
          </Cell>
        ))}
      </Track>
      {row.edgeArrows}
      </RowViewport>
    </Section>
  );
};

export default HubNewGuidesRow;
